import $router from "@/router";
import Vue from "vue";

function initialState() {
  return {
    showTipsIcon: true,
    showTips: false,
    teamsSettings: {},
    loading: {
      default: false,
    },
    customLoading: {
      default: false,
    },
    redirectRoute: null,
    startQuery: null,
    fileStatiBits: {
      UPLOADING: {
        bit: 1,
        readonly: true,
      },
      COMPLETE: {
        bit: 2,
        readonly: true,
      },
      CONVERTING: {
        bit: 4,
        readonly: true,
      },
      CONVERTED: {
        bit: 8,
        readonly: true,
      },
      CONVERSION_ERROR: {
        bit: 16,
        readonly: true,
      },
      VIRUS_SCANNING: {
        bit: 32,
        readonly: true,
      },
      VIRUS_SCAN_RESULT_OK: {
        bit: 64,
        readonly: true,
      },
      VIRUS_SCAN_RESULT_NA: {
        bit: 128,
        readonly: true,
      },
      VIRUS_SCAN_RESULT_INFECTED: {
        bit: 256,
        readonly: true,
      },
      VIRUS_SCAN_ERROR: {
        bit: 512,
        readonly: true,
      },
    },
    signedDocumentStatiBits: {
      NEW: {
        bit: 1,
        readonly: true,
      },
      READY_TO_SIGN: {
        bit: 2,
        readonly: true,
      },
      INIT_SIGNING: {
        bit: 4,
        readonly: true,
      },
      WAITING_ON_SIGNATURES: {
        bit: 8,
        readonly: true,
      },
      SIGNED: {
        bit: 16,
        readonly: true,
      },
      CANCELED: {
        bit: 32,
        readonly: true,
      },
      EXPIRED: {
        bit: 64,
        readonly: true,
      },
      VISIBLE_TO_CLIENT: {
        bit: 128,
        readonly: true,
      },
    },
  };
}

function initialTeamSettings() {
  return {
    unsavedChanges: false,
    pusherInitialized: false,
    lastSelectedDesignTemplate: null,
    visitorSyncTimestamp: null,
    visitorSyncRunning: false,
    redirectedInboxProject: null,
    showInboxProjectData: false,
    inboxNavi: {
      inboxActive: true,
      filterActive: false,
      addFilterActive: false,
      messagesActive: false,
      broadcastsActive: false,
      newBroadcastFilterActive: false,
      isEditMode: false,
      currentPreset: null,
    },
    currentInboxProject: null,
    currentInboxProjectStats: null,
    currentInboxBroadcast: null,
    messagesCounter: 0,
    sidebarIsMinimized: false,
    searchObject: {
      searchQuery: "",
      search: () => {},
      searchInitValue: "",
    },
    projectsCustomFields: null,
    listsSettings: {
      projects: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "updated_at",
          dir: "DESC",
        },
        showAs: {
          firstCol: {
            label: "project.client",
            key: "owner",
            sortable: false,
            format: "owner",
            size: 2,
          },
          secondCol: {
            label: "project.status",
            key: "status",
            sortable: false,
            format: "projectStatus",
            size: 1,
          },
          thirdCol: {
            label: "project.updated",
            key: "updated_at",
            sortable: true,
            format: "date",
            size: 2,
          },
        },
        search: null,
        filterActive: false,
        currentFilterPreset: null,
        currentFilterPresetHasChanged: false,
        customFilters: [],
        filter: {
          is_posted: {
            active: false,
            model: true,
          },
          is_visited: {
            active: false,
            model: true,
          },
          is_mine: {
            active: false,
            model: true,
          },
          is_private: {
            active: false,
            model: true,
          },
          is_archived: {
            active: false,
            model: true,
          },
          has_file_previews: {
            active: false,
            model: true,
          },
          has_uploads: {
            active: false,
            model: true,
          },
          has_downloads: {
            active: false,
            model: true,
          },
          has_file_signings: {
            active: false,
            model: true,
          },
          has_messages: {
            active: false,
            model: true,
          },
          has_any_messages: {
            active: false,
            model: true,
          },
          has_unread_messages: {
            active: false,
            model: true,
          },
          action_required: {
            active: false,
            model: true,
          },
          has_ces_daily_above: {
            active: false,
            model: [],
            select: true,
          },
          with_status: {
            active: false,
            model: [],
            select: true,
          },
          without_status: {
            active: false,
            model: [],
            select: true,
          },
          with_template: {
            active: false,
            model: [],
            select: true,
          },
          without_template: {
            active: false,
            model: [],
            select: true,
          },
          with_user: {
            active: false,
            model: [],
            select: true,
          },
          without_user: {
            active: false,
            model: [],
            select: true,
          },
          with_contact: {
            active: false,
            model: [],
            select: true,
          },
          without_contact: {
            active: false,
            model: [],
            select: true,
          },
          with_tag: {
            active: false,
            model: [],
            select: true,
          },
          without_tag: {
            active: false,
            model: [],
            select: true,
          },
          with_pageviewed: {
            active: false,
            model: [],
            select: true,
          },
          without_pageviewed: {
            active: false,
            model: [],
            select: true,
          },
          time_col: {
            active: false,
            model: [],
            select: true,
          },
          time_relative: {
            active: false,
            model: [],
            select: true,
          },
          time_from: {
            dateTime: true,
            active: false,
            model: "",
          },
          time_to: {
            dateTime: true,
            active: false,
            model: "",
          },
        },
        charts: {
          showCharts: false,
          currentBarChart: null,
          currentBarChartName: null,
        },
      },
      uploads: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "created_at",
          dir: "DESC",
        },
        showAs: {
          firstCol: {
            label: "file.file_extension",
            key: "file_extension",
            sortable: false,
            format: "none",
          },
          secondCol: {
            label: "file.file_size",
            key: "file_size",
            sortable: false,
            format: "size",
          },
          thirdCol: {
            label: "file.created_at",
            key: "created_at",
            sortable: true,
            format: "date",
          },
        },
        search: null,
        filter: null,
      },
      clients: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "updated_at",
          dir: "DESC",
        },
        showAs: {
          firstCol: {
            label: "client.company",
            key: "company",
            sortable: true,
            format: "none",
            size: 2,
          },
          secondCol: {
            label: "client.projects",
            key: "projects_count",
            sortable: true,
            format: "none",
            size: 1,
          },
          thirdCol: {
            label: "client.updated",
            key: "updated_at",
            sortable: true,
            format: "date",
            size: 2,
          },
        },
        search: null,
        filterActive: false,
        currentFilterPreset: null,
        currentFilterPresetHasChanged: false,
        filter: {
          is_mine: {
            active: false,
            model: true,
          },
          with_tag: {
            active: false,
            model: [],
            select: true,
          },
          without_tag: {
            active: false,
            model: [],
            select: true,
          },
        },
      },
      clientImports: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "updated_at",
          dir: "DESC",
        },
        showAs: {
          firstCol: {
            label: "client.company",
            key: "title",
            sortable: true,
            format: "none",
            size: 2,
          },
          secondCol: {
            label: "client.projects",
            key: "user.first_name",
            sortable: true,
            format: "none",
            size: 1,
          },
          thirdCol: {
            label: "client.updated",
            key: "updated_at",
            sortable: true,
            format: "date",
            size: 2,
          },
        },
        search: null,
        filterActive: false,
        filter: {},
      },
      clientProjects: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "updated_at",
          dir: "DESC",
        },
        showAs: {
          firstCol: {
            label: "project.lastActivity",
            key: "last_active_at",
            sortable: true,
            format: "date",
            size: 2,
          },
          secondCol: {
            label: "project.status",
            key: "status",
            sortable: false,
            format: "projectStatus",
            size: 1,
          },
          thirdCol: {
            label: "project.updated",
            key: "updated_at",
            sortable: true,
            format: "date",
            size: 2,
          },
        },
        search: null,
        filterActive: false,
        customFilters: [],
        filter: {
          is_posted: {
            active: false,
            model: true,
          },
          is_visited: {
            active: false,
            model: true,
          },
          is_mine: {
            active: false,
            model: true,
          },
          is_private: {
            active: false,
            model: true,
          },
          is_archived: {
            active: false,
            model: true,
          },
          has_file_previews: {
            active: false,
            model: true,
          },
          has_file_signings: {
            active: false,
            model: true,
          },
          has_uploads: {
            active: false,
            model: true,
          },
          has_downloads: {
            active: false,
            model: true,
          },
          has_messages: {
            active: false,
            model: true,
          },
          with_status: {
            active: false,
            model: [],
            select: true,
          },
          without_status: {
            active: false,
            model: [],
            select: true,
          },
          with_template: {
            active: false,
            model: [],
            select: true,
          },
          without_template: {
            active: false,
            model: [],
            select: true,
          },
          with_contact: {
            active: false,
            model: [],
            select: true,
          },
          without_contact: {
            active: false,
            model: [],
            select: true,
          },
          with_tag: {
            active: false,
            model: [],
            select: true,
          },
          without_tag: {
            active: false,
            model: [],
            select: true,
          },
          with_user: {
            active: false,
            model: [],
            select: true,
          },
          without_user: {
            active: false,
            model: [],
            select: true,
          },
          time_col: {
            active: false,
            model: [],
            select: true,
          },
          time_relative: {
            active: false,
            model: [],
            select: true,
          },
          time_from: {
            dateTime: true,
            active: false,
            model: "",
          },
          time_to: {
            dateTime: true,
            active: false,
            model: "",
          },
        },
      },
      clientSyncs: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "created_at",
          dir: "DESC",
        },
        showAs: {
          firstCol: {
            label: "clientSyncs.list.stopped",
            key: "stopped_at",
            sortable: false,
            format: "none",
          },
        },
        search: null,
        filter: null,
      },
      templates: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "created_at",
          dir: "DESC",
        },
        showAs: {
          firstCol: {
            label: "template.theme",
            key: "theme",
            sortable: false,
            format: "theme",
          },
          secondCol: {
            label: "template.locale",
            key: "locale",
            sortable: true,
            format: "locale",
          },
          thirdCol: {
            label: "template.created_at",
            key: "created_at",
            sortable: true,
            format: "dateFromNow",
          },
        },
        search: null,
        filter: {
          with_locale: {
            active: false,
            model: [],
            select: true,
          },
          without_locale: {
            active: false,
            model: [],
            select: true,
          },
          with_theme: {
            active: false,
            model: [],
            select: true,
          },
          without_theme: {
            active: false,
            model: [],
            select: true,
          },
        },
      },
      templateSnippets: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "updated_at",
          dir: "DESC",
        },
        showAs: {
          firstCol: {
            label: "templateSnippets.type",
            key: "status",
            sortable: true,
            format: "templateSnippetsType",
            size: 2,
          },
          secondCol: {
            label: "templateSnippets.default",
            key: "status",
            sortable: false,
            format: "templateSnippetsDefault",
            size: 1,
          },
          thirdCol: {
            label: "templateSnippets.updated_at",
            key: "updated_at",
            sortable: true,
            format: "date",
            size: 2,
          },
        },
        search: null,
        filterActive: false,
        customFilters: [],
        filter: {
          is_text: {
            active: false,
            model: true,
          },
          is_email: {
            active: false,
            model: true,
          },
          is_default: {
            active: false,
            model: true,
          },
        },
      },
      imports: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "updated_at",
          dir: "DESC",
        },
        showAs: {
          firstCol: {
            label: "stati.status.title",
            key: "status",
            sortable: false,
            format: "formatImportStatus",
          },
          secondCol: {
            label: "imports.updated",
            key: "updated_at",
            sortable: true,
            format: "dateFromNow",
          },
        },
        search: null,
        filter: {},
      },
      contacts: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "last_name",
          dir: "ASC",
        },
        showAs: {
          firstCol: {
            label: "contact.email",
            key: "email",
            sortable: true,
            format: "none",
            size: 2,
          },
          secondCol: {
            label: "contact.locale",
            key: "locale",
            sortable: true,
            format: "locale",
            size: 1,
          },
          thirdCol: {
            label: "contact.created_at",
            key: "created_at",
            sortable: true,
            format: "dateFromNow",
            size: 1,
          },
        },
        search: null,
        filter: {
          // with_locale: {
          //   active: false,
          //   model: [],
          //   select: true
          // },
          // without_locale: {
          //   active: false,
          //   model: [],
          //   select: true
          // },
          // with_theme: {
          //   active: false,
          //   model: [],
          //   select: true
          // },
          // without_theme: {
          //   active: false,
          //   model: [],
          //   select: true
          // }
        },
      },
      users: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "last_name",
          dir: "ASC",
        },
        showAs: {
          firstCol: {
            label: "user.email",
            key: "email",
            sortable: true,
            format: "none",
            size: 2,
          },
          secondCol: {
            label: "user.status",
            key: "status",
            sortable: false,
            format: "userStatus",
            size: 1,
          },
          thirdCol: {
            label: "user.created_at",
            key: "created_at",
            sortable: true,
            format: "dateFromNow",
            size: 1,
          },
        },
        search: null,
        filter: {
          is_invited: {
            active: false,
            model: [],
            select: true,
          },
          is_active: {
            active: false,
            model: [],
            select: true,
          },
        },
      },
      libraryFolders: {
        pagination: {
          page: 1,
          perPage: 1000,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "name",
          dir: "ASC",
        },
      },
      libraryFiles: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "title",
          dir: "ASC",
        },
        showAs: {
          firstCol: {
            label: "library.labels.fileType",
            key: "file_extension",
            sortable: false,
            format: "none",
            size: 1,
          },
        },
      },
      signedDocuments: {
        pagination: {
          page: 1,
          perPage: 5,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "created_at",
          dir: "DESC",
        },
      },
      inbox: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "title",
          dir: "DESC",
        },
        search: "",
        filter: {},
        customFilters: [],
      },
      inboxWithMessages: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        search: "",
        customFilters: [],
        currentFilterPreset: null,
        filter: {
          is_posted: {
            active: false,
            model: true,
          },
          is_visited: {
            active: false,
            model: true,
          },
          is_mine: {
            active: false,
            model: true,
          },
          is_private: {
            active: false,
            model: true,
          },
          is_archived: {
            active: false,
            model: true,
          },
          is_subscribed: {
            active: false,
            model: true,
          },
          has_file_previews: {
            active: false,
            model: true,
          },
          has_uploads: {
            active: false,
            model: true,
          },
          has_downloads: {
            active: false,
            model: true,
          },
          has_file_signings: {
            active: false,
            model: true,
          },
          has_messages: {
            active: false,
            model: true,
          },
          with_status: {
            active: false,
            model: [],
            select: true,
          },
          without_status: {
            active: false,
            model: [],
            select: true,
          },
          with_template: {
            active: false,
            model: [],
            select: true,
          },
          without_template: {
            active: false,
            model: [],
            select: true,
          },
          with_user: {
            active: false,
            model: [],
            select: true,
          },
          without_user: {
            active: false,
            model: [],
            select: true,
          },
          with_contact: {
            active: false,
            model: [],
            select: true,
          },
          without_contact: {
            active: false,
            model: [],
            select: true,
          },
          with_tag: {
            active: false,
            model: [],
            select: true,
          },
          without_tag: {
            active: false,
            model: [],
            select: true,
          },
          with_pageviewed: {
            active: false,
            model: [],
            select: true,
          },
          without_pageviewed: {
            active: false,
            model: [],
            select: true,
          },
          time_col: {
            active: false,
            model: [],
            select: true,
          },
          time_relative: {
            active: false,
            model: [],
            select: true,
          },
          time_from: {
            dateTime: true,
            active: false,
            model: "",
          },
          time_to: {
            dateTime: true,
            active: false,
            model: "",
          },
        },
      },
      formSubmissions: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "created_at",
          dir: "DESC",
        },
        search: null,
        filter: {
          project: {
            active: false,
            model: [],
            select: true,
          },
          client: {
            active: false,
            model: [],
            select: true,
          },
        },
      },
      projectFormSubmissions: {
        pagination: {
          page: 1,
          perPage: 15,
          lastPage: 1,
          total: 0,
        },
        order: {
          col: "created_at",
          dir: "DESC",
        },
        search: null,
        filter: {
          fieldset: {
            active: false,
            model: [],
            select: true,
          },
          client: {
            active: false,
            model: [],
            select: true,
          },
        },
      },
    },
  };
}

const state = initialState;
state.version = 1;

const getters = {
  global_is_default_loading: (state) => {
    return state.loading.default;
  },
  global_is_loading: (state) => {
    return state.loading[$router.currentRoute.params.team] ? state.loading[$router.currentRoute.params.team] : false;
  },
  global_is_custom_loading: (state) => {
    return state.customLoading[$router.currentRoute.params.team]
      ? state.customLoading[$router.currentRoute.params.team]
      : false;
  },
  global_get_version: (state) => {
    return state.version;
  },
  global_get_search_object: (state) => {
    return state.teamsSettings[$router.currentRoute.params.team]
      ? state.teamsSettings[$router.currentRoute.params.team].searchObject
      : null;
  },
  global_get_search_value: (state) => {
    return state.teamsSettings[$router.currentRoute.params.team]
      ? state.teamsSettings[$router.currentRoute.params.team].searchValue
      : "";
  },
  global_get_teams_settings: (state) => {
    return state.teamsSettings;
  },
  global_get_projects_custom_fields: (state) => {
    return state.teamsSettings[$router.currentRoute.params.team]
      ? state.teamsSettings[$router.currentRoute.params.team].projectsCustomFields
      : null;
  },
  global_get_redirect_route: (state) => {
    return state.redirectRoute;
  },
  global_get_start_query: (state) => {
    return state.startQuery;
  },
  global_get_fileStatiBits: (state) => {
    return state.fileStatiBits;
  },
  global_get_signedDocumentStatiBits: (state) => {
    return state.signedDocumentStatiBits;
  },
  global_get_show_inbox_project_data: (state) => {
    return state.teamsSettings[$router.currentRoute.params.team]
      ? state.teamsSettings[$router.currentRoute.params.team].showInboxProjectData
      : null;
  },
  global_get_inbox_navi: (state) => {
    return state.teamsSettings[$router.currentRoute.params.team]
      ? state.teamsSettings[$router.currentRoute.params.team].inboxNavi
      : null;
  },
  global_get_current_inbox_project: (state) => {
    return state.teamsSettings[$router.currentRoute.params.team]
      ? state.teamsSettings[$router.currentRoute.params.team].currentInboxProject
      : null;
  },
  global_get_current_inbox_project_stats: (state) => {
    return state.teamsSettings[$router.currentRoute.params.team]
      ? state.teamsSettings[$router.currentRoute.params.team].currentInboxProjectStats
      : null;
  },
  global_get_current_inbox_broadcast: (state) => {
    return state.teamsSettings[$router.currentRoute.params.team]
      ? state.teamsSettings[$router.currentRoute.params.team].currentInboxBroadcast
      : null;
  },
  global_get_show_tips: (state) => {
    return state.showTips;
  },
  global_get_show_tips_icon: (state) => {
    return state.showTipsIcon;
  },
  global_get_redirected_inbox_project: (state) => {
    return state.teamsSettings[$router.currentRoute.params.team]
      ? state.teamsSettings[$router.currentRoute.params.team].redirectedInboxProject
      : null;
  },
  global_get_last_selected_design_template: (state) => {
    return state.teamsSettings[$router.currentRoute.params.team]
      ? state.teamsSettings[$router.currentRoute.params.team].lastSelectedDesignTemplate
      : null;
  },
  global_get_visitor_sync_timestamp: (state) => {
    return state.teamsSettings[$router.currentRoute.params.team]
      ? state.teamsSettings[$router.currentRoute.params.team].visitorSyncTimestamp
      : null;
  },
  global_get_visitor_sync_running: (state) => {
    return state.teamsSettings[$router.currentRoute.params.team]
      ? state.teamsSettings[$router.currentRoute.params.team].visitorSyncRunning
      : null;
  },
  global_get_unsaved_changes: (state) => {
    return state.teamsSettings[$router.currentRoute.params.team]
      ? state.teamsSettings[$router.currentRoute.params.team].unsavedChanges
      : null;
  },
};

const mutations = {
  GLOBAL_SET_PUSHER_INIT(state, config) {
    if (config.teamUid) {
      state.teamsSettings[config.teamUid].pusherInitialized = config.state;
    }
  },
  GLOBAL_INIT_TEAMS_SETTINGS(state, teamUid) {
    Vue.set(state.teamsSettings, teamUid, initialTeamSettings());
  },
  GLOBAL_DEFAULT_LOADING_STARTED(state) {
    state.loading.default = true;
    console.log("GLOBAL_DEFAULT_LOADING_STARTED");
  },
  GLOBAL_DEFAULT_LOADING_ENDED(state) {
    state.loading.default = false;
    console.log("GLOBAL_DEFAULT_LOADING_ENDED");
  },
  GLOBAL_LOADING_STARTED(state) {
    console.log("GLOBAL_LOADING_STARTED");
    if ($router.currentRoute.params.team) Vue.set(state.loading, $router.currentRoute.params.team, true);
  },
  GLOBAL_LOADING_ENDED(state) {
    console.log("GLOBAL_LOADING_ENDED");
    if ($router.currentRoute.params.team) Vue.set(state.loading, $router.currentRoute.params.team, false);
  },
  GLOBAL_CUSTOM_LOADING_STARTED(state) {
    console.log("GLOBAL_CUSTOM_LOADING_STARTED");
    if ($router.currentRoute.params.team) Vue.set(state.customLoading, $router.currentRoute.params.team, true);
  },
  GLOBAL_CUSTOM_LOADING_ENDED(state) {
    console.log("GLOBAL_CUSTOM_LOADING_ENDED");
    if ($router.currentRoute.params.team) Vue.set(state.customLoading, $router.currentRoute.params.team, false);
  },
  GLOBALE_SIDEBAR_SET_MINIMZED_STATE(state, sidebarState) {
    if (state.teamsSettings) {
      Object.keys(state.teamsSettings).forEach((key) => {
        sidebarState !== null
          ? (state.teamsSettings[key].sidebarIsMinimized = sidebarState)
          : (state.teamsSettings[key].sidebarIsMinimized = !state.teamsSettings[key].sidebarIsMinimized);
      });
    }
  },
  GLOBAL_INIT_SEARCHOBJECT(state, searchObject) {
    state.teamsSettings[$router.currentRoute.params.team].searchObject = searchObject;
  },
  GLOBAL_INIT_SEARCH_VALUE(state, value) {
    state.teamsSettings[$router.currentRoute.params.team].searchValue = value;
  },
  GLOBAL_RESET_SEARCHOBJECT(state) {
    const n = {
      withSearch: false,
      search: () => {},
      searchIcon: "",
      searchText: "",
    };
    if (state.teamsSettings[$router.currentRoute.params.team])
      state.teamsSettings[$router.currentRoute.params.team].searchObject = n;
  },
  GLOBAL_SET_VERSION(state, version) {
    state.version = version;
  },
  GLOBAL_SET_CURRENT_FILTERPRESET(state, data) {
    if (
      state.teamsSettings[$router.currentRoute.params.team].listsSettings &&
      data.list &&
      state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list]
    ) {
      state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].currentFilterPreset =
        data.filterPreset;
    }
  },
  GLOBAL_SET_CURRENT_FILTERPRESET_HAS_CHANGED(state, data) {
    if (
      state.teamsSettings[$router.currentRoute.params.team].listsSettings &&
      data.list &&
      state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list]
    ) {
      state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].currentFilterPresetHasChanged =
        data.state;
    }
  },
  GLOBAL_SET_LISTS_SETTINGS(state, data) {
    if (state.teamsSettings[$router.currentRoute.params.team].listsSettings) {
      if (data.pagination)
        state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].pagination = data.pagination;
      if (data.order) state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].order = data.order;
      state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].search = data.search
        ? data.search
        : "";
      if (data.showAs) {
        if (data.showAs.firstCol)
          state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].showAs.firstCol =
            data.showAs.firstCol;
        if (data.showAs.secondCol)
          state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].showAs.secondCol =
            data.showAs.secondCol;
        if (data.showAs.thirdCol)
          state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].showAs.thirdCol =
            data.showAs.thirdCol;
      }
      if (data.filter) {
        Object.keys(data.filter).forEach((key) => {
          if (state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter[key]) {
            if (data.filter[key] === "" || data.filter[key].length === 0) {
              if (state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter[key].select) {
                state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter[
                  key
                ].active = false;
                state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter[key].model = [];
              } else if (
                state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter[key].dateTime
              ) {
                state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter[
                  key
                ].active = false;
                state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter[key].model = "";
              } else {
                state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter[
                  key
                ].active = false;
                state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter[key].model = true;
              }
            } else if (data.filter[key] !== "") {
              state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter[key].active = true;
              state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter[key].model =
                data.filter[key];
            }
          }
        });
      }
      if (data.customFilters) {
        state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters.forEach(
          (object, arrayKey) => {
            state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters[
              arrayKey
            ].active = false;
            state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters[
              arrayKey
            ].model = [];
            Object.keys(data.customFilters).forEach((customFilterKey) => {
              if (object.name === customFilterKey && data.customFilters[customFilterKey] !== "") {
                state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters[
                  arrayKey
                ].active = true;
                state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters[
                  arrayKey
                ].model = data.customFilters[customFilterKey];
              }
            });
          }
        );
        Object.keys(data.customFilters).forEach((customFilterKey) => {
          state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters.forEach(
            (object, arrayKey) => {
              if (object.name === customFilterKey) {
                if (data.customFilters[customFilterKey] === "") {
                  state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters[
                    arrayKey
                  ].active = false;
                  state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters[
                    arrayKey
                  ].model = [];
                } else if (data.customFilters[customFilterKey] !== "") {
                  state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters[
                    arrayKey
                  ].active = true;
                  state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters[
                    arrayKey
                  ].model = data.customFilters[customFilterKey];
                }
              }
            }
          );
        });
      }
      state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filterActive = false;
      if (state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter) {
        Object.keys(state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter).forEach(
          (key) => {
            if (state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filter[key].active)
              state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filterActive = true;
          }
        );
      }
      if (state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters) {
        state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters.forEach((o, k) => {
          if (state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters[k].active)
            state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].filterActive = true;
        });
      }
      if (data.charts) {
        if (typeof data.charts.showCharts !== "undefined")
          state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].charts.showCharts =
            data.charts.showCharts;
        if (data.charts.currentBarChart)
          state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].charts.currentBarChart =
            data.charts.currentBarChart;
        if (data.charts.currentBarChartName)
          state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].charts.currentBarChartName =
            data.charts.currentBarChartName;
      }
    }
  },
  GLOBAL_ADD_CUSTOM_LISTSETTINGS_FILTER(state, data) {
    console.log(
      "GLOBAL_ADD_CUSTOM_LISTSETTINGS_FILTER",
      data,
      state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list]
    );
    const found = state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters.some(
      (el) => el.name === data.customFilter
    );
    if (!found) {
      let listSettingsCustomFilter = {};
      Vue.set(listSettingsCustomFilter, "name", data.customFilter);
      Vue.set(listSettingsCustomFilter, "active", false);
      Vue.set(listSettingsCustomFilter, "model", []);
      Vue.set(listSettingsCustomFilter, "select", true);
      state.teamsSettings[$router.currentRoute.params.team].listsSettings[data.list].customFilters.push(
        listSettingsCustomFilter
      );
    }
  },
  GLOBAL_SET_PROJECTS_CUSTOM_FIELDS(state, customFields) {
    state.teamsSettings[$router.currentRoute.params.team].projectsCustomFields = customFields;
  },
  GLOBAL_RESET_LISTS_SETTINGS(state) {
    if (
      state.teamsSettings[$router.currentRoute.params.team] &&
      state.teamsSettings[$router.currentRoute.params.team].listsSettings
    ) {
      const s = initialTeamSettings().listsSettings;
      Object.keys(s).forEach((key) => {
        state.teamsSettings[$router.currentRoute.params.team].listsSettings[key].filter = s[key].filter;
        state.teamsSettings[$router.currentRoute.params.team].listsSettings[key].filterActive = s[key].filterActive;
        state.teamsSettings[$router.currentRoute.params.team].listsSettings[key].search = s[key].search;
      });
    }
  },
  GLOBAL_RESET_LIST_SETTINGS(state, list) {
    if (
      state.teamsSettings[$router.currentRoute.params.team] &&
      state.teamsSettings[$router.currentRoute.params.team].listsSettings &&
      state.teamsSettings[$router.currentRoute.params.team].listsSettings[list]
    ) {
      const listSettings = initialTeamSettings().listsSettings[list];
      state.teamsSettings[$router.currentRoute.params.team].listsSettings[list].filter = listSettings.filter;
      state.teamsSettings[$router.currentRoute.params.team].listsSettings[list].pagination = listSettings.pagination;
      state.teamsSettings[$router.currentRoute.params.team].listsSettings[list].filterActive =
        listSettings.filterActive;
      state.teamsSettings[$router.currentRoute.params.team].listsSettings[list].search = listSettings.search;
    }
  },
  GLOBAL_RESET_GLOBAL_TEAM_SETTINGS(state, team) {
    if (team && state.teamsSettings[team]) {
      Vue.set(state.teamsSettings, team, initialTeamSettings());
    }
  },
  GLOBAL_RESET_STORE(state) {
    const s = initialState();
    Object.keys(s).forEach((key) => {
      state[key] = s[key];
    });
  },
  GLOBAL_RESET_LOADING_STATES(state) {
    Object.keys(state.loading).forEach((k) => {
      state.loading[k] = false;
    });
    Object.keys(state.customLoading).forEach((k) => {
      state.customLoading[k] = false;
    });
  },
  GLOBAL_SET_REDIRECT_ROUTE(state, route) {
    state.redirectRoute = route;
  },
  GLOBAL_SET_START_QUERY(state, query) {
    state.startQuery = query;
  },
  GLOBAL_SET_SHOW_TIPS(state, showTips) {
    state.showTips = showTips;
  },
  GLOBAL_SET_INBOX_NAVI(state, data) {
    console.log("GLOBAL STORE GLOBAL_SET_INBOX_NAVI", data);
    switch (data.str) {
      case "inbox":
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.inboxActive = true;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.filterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.addFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newMessageActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.messagesActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastMessagesMobileActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastsActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newBroadcastFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.isEditMode = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.currentPreset = null;
        break;
      case "filter":
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.inboxActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.filterActive = true;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.addFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newMessageActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastsActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newBroadcastFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.messagesActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastMessagesMobileActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.isEditMode = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.currentPreset = null;
        break;
      case "addFilter":
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.inboxActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.filterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.addFilterActive = true;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newMessageActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastsActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newBroadcastFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.messagesActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastMessagesMobileActive = false;
        if (data.isEditMode && data.preset) {
          state.teamsSettings[$router.currentRoute.params.team].inboxNavi.isEditMode = true;
          state.teamsSettings[$router.currentRoute.params.team].inboxNavi.currentPreset = data.preset;
        } else {
          state.teamsSettings[$router.currentRoute.params.team].inboxNavi.isEditMode = false;
          state.teamsSettings[$router.currentRoute.params.team].inboxNavi.currentPreset = null;
        }
        break;
      case "newMessageActive":
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.inboxActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.filterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.addFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newMessageActive = true;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastsActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newBroadcastFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.messagesActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastMessagesMobileActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.isEditMode = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.currentPreset = null;
        break;
      case "messages":
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.messagesActive = true;
        break;
      case "messagesMobile":
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.inboxActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.filterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.addFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newMessageActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.messagesActive = true;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastMessagesMobileActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastsActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newBroadcastFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.isEditMode = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.currentPreset = null;
        break;
      case "broadcastMessagesMobile":
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.inboxActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.filterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.addFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newMessageActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.messagesActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastMessagesMobileActive = true;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastsActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newBroadcastFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.isEditMode = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.currentPreset = null;
        break;
      case "broadcasts":
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.inboxActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.filterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.addFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newMessageActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.messagesActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastMessagesMobileActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastsActive = true;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newBroadcastFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.isEditMode = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.currentPreset = null;
        break;
      case "newBroadcastFilter":
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.inboxActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.filterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.addFilterActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newMessageActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.messagesActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastMessagesMobileActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.broadcastsActive = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.newBroadcastFilterActive = true;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.isEditMode = false;
        state.teamsSettings[$router.currentRoute.params.team].inboxNavi.currentPreset = null;
        break;
    }
  },
  GLOBAL_SET_CURRENT_INBOX_PROJECT(state, project) {
    state.teamsSettings[$router.currentRoute.params.team].currentInboxProject = project;
  },
  GLOBAL_SET_CURRENT_INBOX_PROJECT_STATS(state, stats) {
    state.teamsSettings[$router.currentRoute.params.team].currentInboxProjectStats = stats;
  },
  GLOBAL_SET_CURRENT_INBOX_BROADCAST(state, broadcast) {
    state.teamsSettings[$router.currentRoute.params.team].currentInboxBroadcast = broadcast;
  },
  GLOBAL_SET_SHOW_INBOX_PROJECT_DATA(state, showProjectData) {
    state.teamsSettings[$router.currentRoute.params.team].showInboxProjectData = showProjectData;
  },
  GLOBAL_SET_MESSAGES_COUNTER(state, counter) {
    if ($router.currentRoute.params.team) {
      state.teamsSettings[$router.currentRoute.params.team].messagesCounter = counter;
    }
  },
  GLOBAL_SET_REDIRECTED_INBOX_PROJECT(state, project) {
    state.teamsSettings[$router.currentRoute.params.team].redirectedInboxProject = project;
  },
  GLOBAL_SET_LAST_SELECTED_DESIGN_TEMPLATE(state, lastSelectedDesignTemplate) {
    state.teamsSettings[$router.currentRoute.params.team].lastSelectedDesignTemplate = lastSelectedDesignTemplate;
  },
  GLOBAL_SET_VISITOR_SYNC_TIMESTAMP(state, visitorSyncTimestamp) {
    state.teamsSettings[$router.currentRoute.params.team].visitorSyncTimestamp = visitorSyncTimestamp;
  },
  GLOBAL_SET_VISITOR_SYNC_RUNNING(state, visitorSyncRunning) {
    state.teamsSettings[$router.currentRoute.params.team].visitorSyncRunning = visitorSyncRunning;
  },
  GLOBAL_SET_UNSAVED_CHANGES(state, payload) {
    if (payload.team) {
      state.teamsSettings[payload.team].unsavedChanges = payload.val;
    } else {
      state.teamsSettings[$router.currentRoute.params.team].unsavedChanges = payload.val;
    }
  },
};

const actions = {
  initGlobalTeamSettings({ commit }, teamUid) {
    commit("GLOBAL_INIT_TEAMS_SETTINGS", teamUid);
  },
  setListsSettings({ commit }, data) {
    commit("GLOBAL_SET_LISTS_SETTINGS", data);
  },
  addCustomFilterToListsettings({ commit }, data) {
    commit("GLOBAL_ADD_CUSTOM_LISTSETTINGS_FILTER", data);
  },
  setProjectsCustomFields({ commit }, data) {
    return new Promise((resolve) => {
      commit("GLOBAL_SET_PROJECTS_CUSTOM_FIELDS", data);
      resolve();
    });
  },
  setCurrentFilterPreset({ commit }, payload) {
    commit("GLOBAL_SET_CURRENT_FILTERPRESET", payload);
  },
  setCurrentFilterPresetHasChanged({ commit }, payload) {
    commit("GLOBAL_SET_CURRENT_FILTERPRESET_HAS_CHANGED", payload);
  },
  setRedirectRoute({ commit }, payload) {
    commit("GLOBAL_SET_REDIRECT_ROUTE", payload);
  },
  setStartQuery({ commit }, payload) {
    commit("GLOBAL_SET_START_QUERY", payload);
  },
  toggleShowTips({ commit }, showTips) {
    commit("GLOBAL_SET_SHOW_TIPS", showTips);
  },
  toggleInboxNavi({ commit }, data) {
    commit("GLOBAL_SET_INBOX_NAVI", data);
  },
  setCurrentInboxProject({ commit }, project) {
    commit("GLOBAL_SET_CURRENT_INBOX_PROJECT", project);
  },
  setCurrentInboxProjectStats({ commit }, stats) {
    commit("GLOBAL_SET_CURRENT_INBOX_PROJECT_STATS", stats);
  },
  setCurrentInboxBroadcast({ commit }, broadcast) {
    commit("GLOBAL_SET_CURRENT_INBOX_BROADCAST", broadcast);
  },
  toggleShowInboxProjectData({ commit }, showProjectData) {
    commit("GLOBAL_SET_SHOW_INBOX_PROJECT_DATA", showProjectData);
  },
  setMessagesCounter({ commit }, counter) {
    commit("GLOBAL_SET_MESSAGES_COUNTER", counter);
  },
  setRedirectedInboxProject({ commit }, project) {
    commit("GLOBAL_SET_REDIRECTED_INBOX_PROJECT", project);
  },
  setLastSelectedDesignTemplate({ commit }, lastSelectedDesignTemplate) {
    commit("GLOBAL_SET_LAST_SELECTED_DESIGN_TEMPLATE", lastSelectedDesignTemplate);
  },
  setVisitorSyncTimestamp({ commit }, timestamp) {
    commit("GLOBAL_SET_VISITOR_SYNC_TIMESTAMP", timestamp);
  },
  setVisitorSyncRunning({ commit }, state) {
    commit("GLOBAL_SET_VISITOR_SYNC_RUNNING", state);
  },
  setUnsavedChanges({ commit }, payload) {
    commit("GLOBAL_SET_UNSAVED_CHANGES", payload);
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
